import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import DoneIcon from '@material-ui/icons/Done';
import RemoveIcon from '@material-ui/icons/Remove';
import SearchIcon from '@material-ui/icons/Search';
import DrillxInput from 'components/DrillxInput';
import DrillxPopin from 'components/DrillxPopin';
import { ObjectKeys } from 'helpers/ts-helpers';
import React, { Fragment, memo } from 'react';
import { useIntl } from 'react-intl';

import styles from './FavouritePopin.module.scss';
import FavouriteWellSwitch from './components/FavouriteWellSwitch';
import { useFavouritePopin } from './useFavouritePopin';
import AdvanceSearch from '../AdvanceSearch';

interface Props {
  open: boolean;
  onClose: () => void;
}

const MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH = 2;

const FavouritePopin: React.FC<Props> = ({ open, onClose }) => {
  const intl = useIntl();

  const {
    onPopinClose,
    onSearchInputChanged,
    onClickAdvancedSearch,
    debouncedSearchInput,
    isShowAdvancedSearch,
    onSelectCountries,
    onChangeLiveToggle,
    onChangeReportingToggle,
    onClickClearIcon,
    getCountiesSelectElements,
    selectedCountries,
    isShowLiveWells,
    isShowReportingWells,
    isSelectedAllCheckBox,
    setSelectedAllCheckBox,
    selectedCategorisedWells,
    contriesList,
    filterWells,
    endPage,
    onscroll,
  } = useFavouritePopin({ open, onClose });

  return (
    <DrillxPopin
      {...{ open, onClose: onPopinClose, onscroll }}
      className={styles.popin}
      disableBackdropClick={true}
      ariaLabelledby={intl.formatMessage({ id: 'favouriteWellsPopin.ariaLabelledby' })}
      actions={
        <Button
          variant="contained"
          className={styles.doneButton}
          startIcon={<DoneIcon />}
          onClick={onPopinClose}
        >
          {intl.formatMessage({ id: 'favouriteWellsPopin.done' })}
        </Button>
      }
    >
      <div className={styles.textContainer}>
        <h1>{intl.formatMessage({ id: 'favouriteWellsPopin.welcomeText' })}</h1>
        <p>
          {intl.formatMessage(
            {
              id: 'favouriteWellsPopin.description',
            },
            { minimumNumberOfCharactersInSearch: MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH },
          )}
        </p>
      </div>
      <div>
        <DrillxInput
          placeholder={intl.formatMessage({ id: 'favouriteWellsPopin.search' })}
          className={styles.searchBar}
          onChange={onSearchInputChanged}
          autoFocus
          value={debouncedSearchInput}
          endAdornment={<SearchIcon className={styles.searchIcon} />}
        />
      </div>
      <div>
        <Button
          classes={{
            root: styles.advancedSearchButton,
            startIcon: styles.advancedSearchIcon,
          }}
          color="inherit"
          size="small"
          startIcon={isShowAdvancedSearch ? <RemoveIcon /> : <AddIcon />}
          onClick={() => onClickAdvancedSearch()}
        >
          <span className={styles.advancedSearchText}>Advance Search</span>
        </Button>
      </div>
      {isShowAdvancedSearch && (
        <AdvanceSearch
          onSelectCountries={event => onSelectCountries(event)}
          onChangeLiveToggle={onChangeLiveToggle}
          onChangeReportingToggle={onChangeReportingToggle}
          onClickClearIcon={onClickClearIcon}
          countries={getCountiesSelectElements()}
          selectedCountries={selectedCountries}
          isLiveDataSwitchActive={isShowLiveWells}
          isReportingDataSwitchActive={isShowReportingWells}
          isSelectedAllCheckBox={isSelectedAllCheckBox}
          onClickSelectAllCheckBox={() => setSelectedAllCheckBox(!isSelectedAllCheckBox)}
        />
      )}

      <div onScroll={onscroll} className={styles.wellsContainer}>
        {ObjectKeys(selectedCategorisedWells).map(category =>
          category !== 'wellsByCountry' ? (
            <Fragment key={category}>
              <div className={styles.wellCategoryLabelContainer}>
                {intl.formatMessage({ id: `well-select.categories.${category}` })}
              </div>
              {filterWells(selectedCategorisedWells[category], category).map(well => (
                <div key={well.key} className={styles.wellSwitchContainer}>
                  <FavouriteWellSwitch well={well} />
                </div>
              ))}
            </Fragment>
          ) : (
            <Fragment key={category}>
              <div className={styles.wellCategoryLabelContainer}>
                {intl.formatMessage({ id: `well-select.categories.${category}` })}
              </div>
              {contriesList.map((country, index) => (
                <div key={+index}>
                  <div className={styles.wellSubCategoryLabelContainer}>{country}</div>
                  {filterWells(selectedCategorisedWells[category], category).map((well, idx) =>
                    well.country === country && idx < endPage ? (
                      <div key={well.key} className={styles.wellSwitchContainer}>
                        <FavouriteWellSwitch well={well} />
                      </div>
                    ) : null,
                  )}
                </div>
              ))}
            </Fragment>
          ),
        )}
      </div>
    </DrillxPopin>
  );
};

export default memo(FavouritePopin);
